import {
    addDoc,
    collection,
    doc,
    getDoc,
    getFirestore,
    onSnapshot,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
} from './instrumentedFirestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './app';
import { COLLECTIONS, feeChargeDocPath } from '@constants/firestorePaths';
import { ENV } from '@configs/env';

function db() {
    return getFirestore(getFirebaseApp());
}

function stripUndefinedDeep(value) {
    if (value === undefined) return undefined;
    if (value === null) return null;
    if (typeof value?.toDate === 'function') return value;
    if (Array.isArray(value)) return value.map(stripUndefinedDeep).filter((v) => v !== undefined);
    if (typeof value === 'object') {
        const proto = Object.getPrototypeOf(value);
        const isPlain = proto === Object.prototype || proto === null;
        if (!isPlain) return value;
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            const next = stripUndefinedDeep(v);
            if (next !== undefined) out[k] = next;
        }
        return out;
    }
    return value;
}

function normalizeDateInput(value) {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value.toDate();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

export const FEE_CHARGE_CATEGORIES = {
    ADMISSION: 'admission',
    TUITION: 'tuition',
    OTHER: 'other',
};

export const FEE_CHARGE_FREQUENCIES = {
    ONE_TIME: 'one_time',
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
    CUSTOM_MONTHS: 'custom_months',
};

export function subscribeFeeCharges({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FEE_CHARGES);
    const q = query(ref, orderBy('createdAt', 'desc'));
    return onSnapshot(
        q,
        (snap) => onNext?.(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => onError?.(err)
    );
}

export function subscribeFeeChargeHistory({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FEE_CHARGE_HISTORY);
    const q = query(ref, orderBy('createdAt', 'desc'));
    return onSnapshot(
        q,
        (snap) => onNext?.(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => onError?.(err)
    );
}

export function subscribeStudentFeePayments({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.STUDENT_FEE_PAYMENTS);
    const q = query(ref, orderBy('createdAt', 'desc'));
    return onSnapshot(
        q,
        (snap) => onNext?.(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => onError?.(err)
    );
}

export async function upsertFeeCharge({ tenantId, chargeId, payload, actor }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!chargeId && !payload?.name) throw new Error('Charge name is required');
    const now = serverTimestamp();
    const localNow = new Date();

    const ref = chargeId
        ? doc(db(), feeChargeDocPath(tenantId, chargeId))
        : doc(collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FEE_CHARGES));

    const nextPayload = stripUndefinedDeep({
        ...(payload?.tenantId === undefined ? { tenantId } : { tenantId: payload.tenantId }),
        ...(payload?.name !== undefined ? { name: String(payload.name || '').trim() } : {}),
        ...(payload?.category !== undefined ? { category: payload.category || FEE_CHARGE_CATEGORIES.OTHER } : {}),
        ...(payload?.amount !== undefined ? { amount: Number(payload.amount || 0) || 0 } : {}),
        ...(payload?.frequency !== undefined ? { frequency: payload.frequency || FEE_CHARGE_FREQUENCIES.MONTHLY } : {}),
        ...(payload?.intervalMonths !== undefined ? { intervalMonths: Number(payload.intervalMonths || 1) || 1 } : {}),
        ...(payload?.applyScope !== undefined ? { applyScope: payload.applyScope || 'all_grades' } : {}),
        ...(payload?.gradeList !== undefined ? { gradeList: Array.isArray(payload.gradeList) ? payload.gradeList.filter(Boolean) : [] } : {}),
        ...(payload?.gradeLabels !== undefined ? { gradeLabels: Array.isArray(payload.gradeLabels) ? payload.gradeLabels.filter(Boolean) : [] } : {}),
        ...(payload?.implementationDate !== undefined ? { implementationDate: normalizeDateInput(payload.implementationDate) || new Date() } : {}),
        ...(payload?.notes !== undefined ? { notes: String(payload.notes || '').trim() } : {}),
        ...(payload?.active !== undefined ? { active: payload.active !== false } : {}),
        updatedAt: now,
        updatedAtLocal: localNow,
    });

    const prevSnap = await getDoc(ref);
    const prevData = prevSnap.exists() ? prevSnap.data() : null;
    if (chargeId) {
        const meaningfulKeys = Object.keys(nextPayload || {}).filter(
            (k) => !['updatedAt', 'updatedAtLocal', 'tenantId'].includes(k)
        );
        if (meaningfulKeys.length === 0) {
        throw new Error('No changes to update');
        }
    }

    await runTransaction(db(), async (tx) => {
        tx.set(
            ref,
            {
                ...nextPayload,
                ...(prevData ? {} : { createdAt: now, createdAtLocal: localNow }),
            },
            { merge: true }
        );
    });

    const historyRef = collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FEE_CHARGE_HISTORY);
    await addDoc(historyRef, {
        tenantId,
        chargeId: ref.id,
        action: prevData ? 'updated' : 'created',
        actorName: actor?.name || actor?.email || 'Unknown',
        actorEmail: actor?.email || null,
        before: prevData || null,
        after: { ...(prevData || {}), ...nextPayload },
        createdAt: now,
        createdAtLocal: localNow,
    });

    return { chargeId: ref.id };
}

export async function recordStudentFeePayment({ tenantId, payment }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!payment?.studentId) throw new Error('Missing studentId');
    if (!payment?.chargeId) throw new Error('Missing chargeId');
    const amount = Number(payment?.amount || 0) || 0;
    if (amount <= 0) throw new Error('Amount must be greater than 0');

    const ref = collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.STUDENT_FEE_PAYMENTS);
    await addDoc(ref, {
        tenantId,
        studentId: payment.studentId,
        chargeId: payment.chargeId,
        amount,
        paidAt: normalizeDateInput(payment.paidAt) || new Date(),
        note: String(payment.note || '').trim(),
        createdAt: serverTimestamp(),
        createdBy: payment.createdBy || null,
    });
}

export async function sendFeeInvoiceNotification({ tenantId, payload }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const backendUrl = ENV.WHATSAPP_BACKEND?.SEND_INVOICE_URL;
    if (backendUrl) {
        const res = await fetch(backendUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(ENV.WHATSAPP_BACKEND?.API_KEY ? { 'X-API-Key': ENV.WHATSAPP_BACKEND.API_KEY } : {}),
            },
            body: JSON.stringify({
                tenantId,
                ...(payload || {}),
            }),
        });
        let json = null;
        try {
            json = await res.json();
        } catch {
            json = null;
        }
        if (!res.ok) {
            throw new Error(json?.error || `Backend send failed (HTTP ${res.status})`);
        }
        return json;
    }

    const fnName = ENV.FIREBASE?.SEND_FEE_INVOICE_FUNCTION_NAME;
    if (!fnName) {
        throw new Error('SEND_INVOICE_FUNCTION_NOT_CONFIGURED');
    }
    const callable = httpsCallable(getFunctions(getFirebaseApp()), fnName);
    const res = await callable({ tenantId, ...(payload || {}) });
    return res?.data || null;
}
